import { useEffect, useState } from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
}

export default function Pagination({ currentPage, totalPages, onChange }: PaginationProps) {
  const [jumpValue, setJumpValue] = useState("");

  useEffect(() => {
    setJumpValue("");
  }, [currentPage]);

  if (totalPages <= 1) return null;

  const pages = Array.from({ length: totalPages }, (_, index) => index + 1);

  const goTo = (page: number) => {
    const safe = Math.min(Math.max(page, 1), totalPages);
    onChange(safe);
  };

  const handleJump = () => {
    const parsed = Number(jumpValue);
    if (!Number.isFinite(parsed) || parsed < 1) return;
    goTo(Math.floor(parsed));
    setJumpValue("");
  };

  return (
    <nav className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4" aria-label="分页导航">
      <div className="flex items-center gap-1.5 flex-wrap justify-center">
        <button
          type="button"
          onClick={() => goTo(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-9 px-3 rounded-md border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          <span className="inline-flex items-center gap-1">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-s-line" aria-hidden="true" />
            </span>
            上一页
          </span>
        </button>
        {pages.map((page) => (
          <button
            key={page}
            type="button"
            onClick={() => goTo(page)}
            aria-current={page === currentPage ? "page" : undefined}
            className={`w-9 h-9 rounded-md text-sm font-label transition-colors cursor-pointer ${
              page === currentPage
                ? "bg-primary-600 text-background-50"
                : "border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
            }`}
          >
            {page}
          </button>
        ))}
        <button
          type="button"
          onClick={() => goTo(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-9 px-3 rounded-md border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          <span className="inline-flex items-center gap-1">
            下一页
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </span>
          </span>
        </button>
      </div>

      <div className="flex items-center gap-2 text-sm text-foreground-500">
        <span className="font-label">
          第 {currentPage} / {totalPages} 页
        </span>
        <span className="w-px h-4 bg-background-300" />
        <input
          value={jumpValue}
          onChange={(event) => setJumpValue(event.target.value.replace(/[^\d]/g, ""))}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleJump();
          }}
          inputMode="numeric"
          aria-label="输入页码"
          placeholder="页码"
          className="w-16 h-9 px-2 rounded-md border border-background-200 bg-background-50 text-sm text-center focus:outline-none focus:border-primary-400"
        />
        <button
          type="button"
          onClick={handleJump}
          className="h-9 px-3 rounded-md bg-secondary-100 text-foreground-800 text-sm hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          跳转
        </button>
      </div>
    </nav>
  );
}